/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React, {useEffect} from "react";
// redux hooks
import { useFieldArray, Controller, useFormContext, useWatch } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../@BASE/utils/BASEUtils';
// material-ui components
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
// custom components
import FormEmptyListing from "./FormEmptyListing";
import DeleteModal from "../crud/DeleteModal";

export default function FormSectionsArray( { controlName, displayName, addObject, children } ) {
  // get form methods from context
  const {
    control,
    formState: { errors },
  } = useFormContext();
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: controlName,
  });
  // manage tab state
  const [activeSectionTab, setActiveSectionTab] = React.useState(0);
  // ensure active tab is valid
  useEffect(() => {
    setActiveSectionTab((t) => Math.min(t, Math.max(0, fields.length - 1)));
  }, [fields.length]);

  const activeSection = fields[activeSectionTab];
  const activeKey = fields[activeSectionTab]?.id ?? activeSectionTab;

  // add a new section
  const addSection = () => {
    append(addObject)
    setActiveSectionTab(fields.length);
  }
  // remove a section
  const removeSection = (index) => {
    remove(index)
    setActiveSectionTab((prev) => {
      if (index < prev) return prev - 1; 
      if (index === prev) return Math.max(0, prev - 1);
      return prev;
    });
  }

  return (
    <div className="mt-32 w-full">
      {activeSection ? (
        <>
          <div className="flex w-full items-center justify-between gap-16">
            <Tabs
              value={activeSectionTab}
              onChange={(_, v) => setActiveSectionTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              classes={{ root: "grow"}}
            >
              {fields.map((field, index) => (
                <Tab
                  key={field.id}
                  value={index}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{`${displayName} ${index + 1}`}</span>
                    </Box>
                  }
                />
              ))}
            </Tabs>
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={addSection}
              startIcon={<BASESvgIcon size={20}>heroicons-outline:plus</BASESvgIcon>}
            >
              Add {displayName}
            </Button>
          </div>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardHeader
              title={
                <Typography className="text-2xl font-bold tracking-tight leading-6 truncate">{`${displayName} ${activeSectionTab + 1}`}</Typography>
              }
              action={
                <Stack direction="row" spacing={3}>
                  <Button
                    disabled={activeSectionTab === 0}
                    onClick={() => {
                      move(activeSectionTab, activeSectionTab - 1);
                      setActiveSectionTab((t) => t - 1);
                    }}
                    startIcon={
                      <BASESvgIcon size={20}>heroicons-outline:arrow-left</BASESvgIcon>
                    }
                  >
                    Move left
                  </Button>
                  <Button
                    disabled={activeSectionTab === fields.length - 1}
                    onClick={() => {
                      move(activeSectionTab, activeSectionTab + 1);
                      setActiveSectionTab((t) => t + 1);
                    }}
                    endIcon={
                      <BASESvgIcon size={20}>heroicons-outline:arrow-right</BASESvgIcon>
                    }
                  >
                    Move right
                  </Button>
                  <DeleteModal 
                    onDelete={() => {removeSection(activeSectionTab)}}
                    resource="survey section" 
                  />
                </Stack>
              }
            />
            <CardContent
              key={`section-${activeKey}`}
            >
              {typeof children === "function"
                ? children({
                    index: activeSectionTab,
                    activeSection,
                    activeKey,
                    fields,
                    errors,
                    control,
                  })
                : children}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <FormEmptyListing
              entity={displayName}
              addEntity={addSection}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}